import * as React from 'react'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'

interface FormFieldProps {
  /** Becomes the control's id and the base for the helper/error ids. */
  id: string
  label: React.ReactNode
  required?: boolean
  helper?: React.ReactNode
  error?: React.ReactNode
  className?: string
  /**
   * Render prop receives the props to spread on the control:
   * `{ id, aria-invalid, aria-describedby, aria-required }`.
   */
  children: (controlProps: {
    id: string
    'aria-invalid': boolean | undefined
    'aria-describedby': string | undefined
    'aria-required': boolean | undefined
  }) => React.ReactNode
}

/**
 * Label on top, control, then helper text or an inline error with an icon.
 *
 *   <FormField id="mobile" label="Mobile number" required helper="10 digits" error={errors.mobile}>
 *     {(props) => <Input {...props} inputMode="numeric" />}
 *   </FormField>
 */
function FormField({ id, label, required, helper, error, className, children }: FormFieldProps) {
  const describedBy = error ? `${id}-error` : helper ? `${id}-help` : undefined
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })}
      {error ? (
        <p id={`${id}-error`} className="flex items-start gap-1 text-base text-error">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : helper ? (
        <p id={`${id}-help`} className="text-base text-text-muted">
          {helper}
        </p>
      ) : null}
    </div>
  )
}

export { FormField }
